import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ClickOutside } from '../../shared/directives/click-outside';
import { AuthService } from '../../core/services/auth.service';
import { OverlayModule } from '@angular/cdk/overlay';
import Users from '../../core/services/users.service';
import { AsyncPipe, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { ProfileService } from '../../core/services/profile.service';
import { filter, map, shareReplay, startWith, tap } from 'rxjs';
import { NotificationsComponent } from '../notifications/notifications.component';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { WebsocketService } from '../../core/services/websocket.service';
import { Website } from '../../shared/models/website.models';
import { StatusLabelComponent } from '../../shared/components/status-label/status-label.component';
import { WebsiteStoreService } from '../../core/services/website-store.service';
import { TaskProgressStatus } from '../../shared/models/backups.models';
import { Status } from '../../shared/models/status-label.model';
import { WebsiteGroupRecord } from '../../shared/models/website-group.models';
import { GroupData } from '../../shared/models/group-overview.model';

@Component({
  selector: 'app-header',
  imports: [
    FontAwesomeModule,
    MatButtonModule,
    MatBadgeModule,
    MatSlideToggleModule,
    ClickOutside,
    OverlayModule,
    AsyncPipe,
    TitleCasePipe,
    NotificationsComponent,
    NgTemplateOutlet,
    StatusLabelComponent,
    RouterLink,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header implements OnInit {
  private authService = inject(AuthService);

  private usersService = inject(Users);

  private profileService = inject(ProfileService);

  private websocketService = inject(WebsocketService);

  private websiteStore = inject(WebsiteStoreService);

  private router = inject(Router);

  currentUser$ = this.usersService
    .getUser(this.authService.getUser()?.id || '')
    .pipe(shareReplay(1));

  currentUserProfile$ = this.profileService
    .getBaseProfileDataByUserId(this.authService.getUser()?.id || '')
    .pipe(shareReplay(1));

  userName$ = this.currentUserProfile$.pipe(map((p) => `${p.firstName} ${p.lastName}`));

  userRole$ = this.currentUser$.pipe(map((u) => u.role.name));

  notifications$ = this.profileService.getProfileNotifications().pipe(
    tap(() => (this.loading = false)),
    shareReplay(1),
  );

  isUserMenuExpanded = false;

  isNotificationsOpen = false;

  loading = true;

  showAdditionalTitleInfo = signal(false);

  currentPath$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    startWith({
      urlAfterRedirects: this.router.url,
    } as NavigationEnd),
    map((event: NavigationEnd) => event.urlAfterRedirects),
  );

  snapshot$ = this.currentPath$.pipe(
    map(() => {
      let route = this.router.routerState.root;

      while (route.firstChild) {
        route = route.firstChild;
      }

      return route.snapshot;
    }),
  );

  title$ = this.snapshot$.pipe(
    map((snapshot) => {
      if (snapshot.title === 'Website') {
        this.showAdditionalTitleInfo.set(true);
        return this.getWebsiteTitle(snapshot.data['websiteData'].website);
      } else if (snapshot.title === 'Restore Details') {
        this.showAdditionalTitleInfo.set(true);
        return this.getWebsiteRestoreTitle(snapshot.data['restoreData']['websiteData'].website);
      } else if (snapshot.title === 'Group') {
        this.showAdditionalTitleInfo.set(true);
        return this.getGroupTitle(snapshot.data['groupData']);
      } else if (snapshot.title === 'Usage Details') {
        this.showAdditionalTitleInfo.set(true);
        return this.getGroupUsageTitle(snapshot.data['group']);
      }

      this.showAdditionalTitleInfo.set(false);
      return snapshot.title ?? 'No title';
    }),
  );

  data$ = this.snapshot$.pipe(map((s) => s.data));

  websiteData = this.websiteStore.websiteData;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly SITE_TYPES: Record<any, string> = {
    MAIN: 'Main',
    SUB_WEBSITE: 'Site',
    ALIAS: 'Alias',
    STAGING: 'Staging',
  };

  ngOnInit(): void {
    this.websocketService.listen('notification').subscribe();
  }

  expandMenu(): void {
    this.isUserMenuExpanded = !this.isUserMenuExpanded;
  }

  closeUserMenu(): void {
    this.isUserMenuExpanded = false;
  }

  openCloseNotifications(): void {
    this.isNotificationsOpen = !this.isNotificationsOpen;
  }

  logout(): void {
    this.authService.logout();
  }

  getWebsiteTitle(website: Website): string {
    return `Website - ${website.domain}`;
  }

  getWebsiteRestoreTitle(website: Website): string {
    return `Restore Details - ${website.domain}`;
  }

  getGroupTitle(group: GroupData): string {
    return `Group - ${group.websiteGroup.mainWebsite?.domain || 'Unknown'}`;
  }

  getGroupUsageTitle(group: WebsiteGroupRecord): string {
    const mainWebsite = group.website?.domain;
    return `Usage details - ${mainWebsite || 'Unknown'}`;
  }

  toStatus(key: TaskProgressStatus): Status {
    switch (key) {
      case TaskProgressStatus.Finished:
        return Status.FINISHED;
      case TaskProgressStatus.Pending:
        return Status.PENDING;
      case TaskProgressStatus.Running:
        return Status.IN_PROGRESS;
      default:
        return Status.FAILED;
    }
  }
}
